import { environment } from 'src/environments/environment';

/**
 * Per-host site configuration.
 *
 * A release is built ONCE (production config) and the same files are deployed
 * to staging.platex.co.uk for review and then copied unchanged to
 * www.platex.co.uk. Everything that must differ between those hosts (which
 * backend the app talks to, whether unfinished DEV2 work is shown) is decided
 * here from the exact hostname, before any route or service is created.
 *
 *  - dev2.platex.co.uk   ongoing development: unfinished work ON, backend as
 *                        built (environment.prod.ts + the Amplify buildSpec).
 *  - staging.platex.co.uk release candidate: unfinished work OFF, backend =
 *                        same-origin /api and /search-api, which the staging
 *                        CloudFront distribution proxies to the DEV2 API and
 *                        the isolated DEV2 search Lambda. Never production.
 *  - www.platex.co.uk    production: unfinished work OFF, production backend.
 *  - localhost           local development: as environment.ts.
 *  - anything else on a production build (S3 website endpoint, a bare
 *    CloudFront domain, a copied bundle): FAIL CLOSED, no backend at all.
 *
 * Keep this module free of Angular imports: app.module evaluates it while
 * building the route table.
 */
export type SiteName = 'dev2' | 'staging' | 'production' | 'local' | 'unknown';

export interface SiteProfile {
  name: SiteName;
  /** Unfinished DEV2 work (specialist redesigns, account v2, live auctions…). */
  unfinished: boolean;
  /**
   * Account v2 (saved plates, tracked plates, saved searches, recently viewed, My Registrations)
   * instead of the legacy account page. Needs the /api/account endpoints, which production's API
   * gains in Stage C before the site goes live.
   */
  accountV2: boolean;
  /** Historic advert clippings on the plate timeline (owner approved for launch, 2026-11-03). */
  archiveAdverts: boolean;
  /** Registry vehicle history (row-data). Withheld until a read-only endpoint exists. */
  vehicleHistory: boolean;
  /** Compact launch navigation and footer instead of the DEV2 mega menu. */
  launchNav: boolean;
  /**
   * Plato chat assistant: 'admin' = DEV2 admins only (session token checked by the assistant),
   * 'edge' = everyone who reaches the site (staging: behind basic auth; the assistant only answers
   * requests that came through this site's CloudFront), false = not shown.
   */
  assistant: 'admin' | 'edge' | false;
  /**
   * The account asks the API for production's alert settings ('legacy-alerts'): tracked plates
   * and saved searches as the existing alert jobs process them. Unset on DEV2, which keeps the
   * API's own default (market events). The API accepts it only from its allowed list.
   */
  accountAlerts?: 'legacy-alerts';
  /**
   * PlateX Market Guides (/market-guides): library, guide landing pages and the reader.
   * Off on www until the owner approves the release.
   */
  marketGuides: boolean;
  /** What's on Your Plate (/whats-on-your-plate): draft initiative page, never on www unapproved. */
  whatsOnYourPlate: boolean;
}

const BLOCKED = 'blocked.invalid';

// Backend overrides applied to `environment` for release hosts. Keys not listed
// keep their built value. Endpoints a release must never call are blanked.
const RELEASE_COMMON = {
  mode2BaseAddress: '',
  shadowLogBaseAddress: '',
  namesRegisterDev2BaseAddress: '',
  editorialEnabled: false,
  editorialApiBaseAddress: '',
  assistantUrl: '',
  plateInboxUrl: '',
  plateInboxToken: '',
  useLiveV2: false,
  liveV2CompareEnabled: false,
};

const STAGING_BACKEND = {
  ...RELEASE_COMMON,
  // Plato apprentice (platex-assistant-staging), proxied same-origin by the staging distribution.
  assistantUrl: '/assistant-api',
  apiProtocol: 'https://',
  apiBaseAddress: 'staging.platex.co.uk/api',
  dataApiProtocol: 'https://',
  dataApiBaseAddress: 'staging.platex.co.uk/api',
  platoProtocol: 'https://',
  platoBaseAddress: BLOCKED,
  platoSearchProtocol: 'https://',
  platoSearchBaseAddress: 'staging.platex.co.uk/search-api/search',
  platoSearchCanonicalProtocol: 'https://',
  platoSearchCanonicalBaseAddress: 'staging.platex.co.uk/search-api/search',
  // Names register / finder: the read-only Names API (static daily artifact,
  // no database, no writes). Not the main search Lambda, which has no names processes.
  namesRegisterDev2BaseAddress: '13dfkzcsnh.execute-api.eu-west-2.amazonaws.com/search',
};

const PRODUCTION_BACKEND = {
  ...RELEASE_COMMON,
  apiProtocol: 'https://',
  apiBaseAddress: 'api.platex.co.uk/api',
  dataApiProtocol: 'https://',
  dataApiBaseAddress: 'api.platex.co.uk/api',
  platoProtocol: 'https://',
  platoBaseAddress: 'plato.platex.co.uk',
  platoSearchProtocol: 'https://',
  platoSearchBaseAddress: '81ugpzvec7.execute-api.eu-west-2.amazonaws.com/Prod/search',
  platoSearchCanonicalProtocol: 'https://',
  platoSearchCanonicalBaseAddress: '81ugpzvec7.execute-api.eu-west-2.amazonaws.com/Prod/search',
  // Names register / finder: the read-only Names API (static daily artifact,
  // no database, no writes). Not the main search Lambda, which has no names processes.
  namesRegisterDev2BaseAddress: '13dfkzcsnh.execute-api.eu-west-2.amazonaws.com/search',
};

const UNKNOWN_BACKEND = {
  ...RELEASE_COMMON,
  apiBaseAddress: BLOCKED,
  dataApiBaseAddress: BLOCKED,
  platoBaseAddress: BLOCKED,
  platoSearchBaseAddress: BLOCKED,
  platoSearchCanonicalBaseAddress: BLOCKED,
};

function resolveSite(): { profile: SiteProfile; backend: { [key: string]: any } | null } {
  const host = typeof window !== 'undefined' && window.location ? window.location.hostname : '';
  const release = { unfinished: false, accountV2: true, archiveAdverts: true, vehicleHistory: false, launchNav: true, assistant: false as const, accountAlerts: 'legacy-alerts' as const, marketGuides: false, whatsOnYourPlate: false };
  const dev = { unfinished: true, accountV2: true, archiveAdverts: true, vehicleHistory: true, launchNav: false, assistant: 'admin' as const, marketGuides: true, whatsOnYourPlate: true };
  switch (host) {
    case 'dev2.platex.co.uk':
    case 'dev2.localhost':
      return { profile: { name: 'dev2', ...dev }, backend: null };
    case 'staging.platex.co.uk':
      // Market Guides are under review here only.
      return { profile: { name: 'staging', ...release, assistant: 'edge', marketGuides: true, whatsOnYourPlate: true }, backend: STAGING_BACKEND };
    case 'www.platex.co.uk':
    case 'platex.co.uk':
      // What's on Your Plate launches with the site (owner, 4 November 2026); Market Guides follows
      // once production's API has /api/Publications and migration 017 (owner approval required).
      return { profile: { name: 'production', ...release, whatsOnYourPlate: true, marketGuides: true }, backend: PRODUCTION_BACKEND };
    case 'localhost':
    case '127.0.0.1':
      if (!environment.production) {
        return { profile: { name: 'local', ...dev }, backend: null };
      }
      // A production build served locally behaves like the release.
      return { profile: { name: 'local', ...release }, backend: null };
  }
  if (!environment.production) {
    return { profile: { name: 'local', ...dev }, backend: null };
  }
  return { profile: { name: 'unknown', ...release }, backend: UNKNOWN_BACKEND };
}

const resolved = resolveSite();
if (resolved.backend) Object.assign(environment as any, resolved.backend);

// Staging only: the DEV2 API builds dealer links on the PRODUCTION tracking
// redirect (https://api.platex.co.uk/api/tracking/dealer/...). Those open via
// window.open or plain links, outside HttpClient, so they are rewritten here
// to staging's own /api (the DEV2 API) and can never record a production
// dealer click. Any other production URL is refused outright.
if (resolved.profile.name === 'staging' && typeof window !== 'undefined') {
  const PROD_API = /^https?:\/\/(api\.platex\.co\.uk|www\.platex\.co\.uk|platex\.co\.uk|plato\.platex\.co\.uk)\/api\//i;
  const PROD_HOST = /^https?:\/\/([a-z0-9.-]*\.)?(api|plato)\.platex\.co\.uk\b|execute-api\.eu-west-2\.amazonaws\.com/i;
  const safe = (url: string): string | null => {
    const u = String(url || '');
    if (PROD_API.test(u)) return u.replace(PROD_API, '/api/');
    return PROD_HOST.test(u) ? null : u;
  };
  const open = window.open.bind(window);
  window.open = ((url?: string | URL, target?: string, features?: string) => {
    const next = url == null ? url : safe(String(url));
    if (next === null) {
      console.warn('[staging] blocked production URL', url);
      return null;
    }
    return open(next as any, target, features);
  }) as typeof window.open;
  document.addEventListener(
    'click',
    (e) => {
      const a = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a) return;
      const next = safe(a.href);
      if (next === null) {
        e.preventDefault();
        console.warn('[staging] blocked production link', a.href);
      } else if (next !== a.href) {
        a.href = next;
      }
    },
    true
  );
}

export const site: SiteProfile = resolved.profile;

/** Unfinished DEV2 work is visible (dev2 host and local dev builds only). */
export const showUnfinished: boolean = site.unfinished;

/** Historic advert clippings on the plate timeline (every host). */
export const archiveAdvertsEnabled: boolean = site.archiveAdverts;

/** Account v2 instead of the legacy account page (every host since the 2026-11-03 release). */
export const accountV2Enabled: boolean = site.accountV2;

/** Alert profile the account asks the API for (X-Account-Profile), or null for the API's default. */
export const accountAlertProfile: string | null = site.accountAlerts || null;
/** Market Guides routes, links and features (dev2, local, staging; not www until approved). */
export const marketGuidesEnabled: boolean = site.marketGuides;

/** What's on Your Plate draft page (dev2, local, staging; not www until approved). */
export const whatsOnYourPlateEnabled: boolean = site.whatsOnYourPlate;
